import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del, uploadFile } from "../client";
import type {
  BiblioRecord,
  BiblioRecordSummary,
  CatalogSearchParams,
  CreateBiblioRecordInput,
  RdaVocabularies,
  SearchFacets,
  UpdateBiblioRecordInput,
} from "../types";

// Import-related types
export interface ImportFormat {
  name: string;
  description: string;
  extensions: string[];
  mimeTypes?: string[];
}

export interface ImportValidationResult {
  valid: boolean;
  error?: string;
  fileName?: string;
  fileSize?: number;
  estimatedRecords?: number;
  sample?: {
    title?: string;
    isbn?: string;
  };
  warnings?: string[];
}

export interface ImportReport {
  sourceId: string;
  totalProcessed: number;
  successCount: number;
  createdCount: number;
  updatedCount: number;
  errorCount: number;
  /** Records whose RDA-FR types were not all declared by the source and were inferred. */
  rdaInferredCount: number;
  /** Records given a BnF ARK in 003 (Transition bibliographique). */
  arkAssignedCount: number;
  /** Records with an ISBN for which the BnF returned no single match. */
  arkUnresolvedCount: number;
  successRate: number;
  successes?: Array<{ index: number; title: string; action: string }>;
  errors?: string[];
}

export interface ImportResult {
  success: boolean;
  dryRun: boolean;
  report: ImportReport;
  error?: string;
}

const RECORDS_KEY = "catalog-records";
const FACETS_KEY = "catalog-facets";

/**
 * Search catalog records.
 */
export function useCatalogSearch(params: CatalogSearchParams) {
  const queryParams = new URLSearchParams();

  if (params.q) queryParams.set("q", params.q);
  if (params.type) queryParams.set("type", params.type);
  if (params.language) queryParams.set("language", params.language);
  if (params.yearFrom) queryParams.set("yearFrom", String(params.yearFrom));
  if (params.yearTo) queryParams.set("yearTo", String(params.yearTo));
  if (params.author) queryParams.set("author", params.author);
  if (params.publisher) queryParams.set("publisher", params.publisher);
  if (params.page) queryParams.set("page", String(params.page));
  if (params.limit) queryParams.set("limit", String(params.limit));

  const queryString = queryParams.toString();

  return useQuery({
    queryKey: [RECORDS_KEY, "search", params],
    queryFn: async () => {
      const url = queryString ? `/catalog/records?${queryString}` : "/catalog/records";
      return get<BiblioRecordSummary[]>(url);
    },
  });
}

/**
 * Get a single record by ID.
 */
export function useBiblioRecord(id: string | undefined) {
  return useQuery({
    queryKey: [RECORDS_KEY, id],
    queryFn: async () => {
      if (!id) throw new Error("ID is required");
      return get<BiblioRecord>(`/catalog/records/${id}`);
    },
    enabled: !!id,
  });
}

/**
 * Get search facets.
 */
export function useCatalogFacets(query?: string) {
  return useQuery({
    queryKey: [FACETS_KEY, query],
    queryFn: async () => {
      const url = query
        ? `/catalog/records/facets?q=${encodeURIComponent(query)}`
        : "/catalog/records/facets";
      return get<SearchFacets>(url);
    },
  });
}

/**
 * Create a new record.
 */
export function useCreateBiblioRecord() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateBiblioRecordInput) => {
      return post<BiblioRecord>("/catalog/records", input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [RECORDS_KEY] });
    },
  });
}

/**
 * Update an existing record.
 */
export function useUpdateBiblioRecord(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateBiblioRecordInput) => {
      return patch<BiblioRecord>(`/catalog/records/${id}`, input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [RECORDS_KEY] });
    },
  });
}

/**
 * Delete a record.
 */
export function useDeleteBiblioRecord() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await del(`/catalog/records/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [RECORDS_KEY] });
    },
  });
}

// ============================================================
// RDA-FR vocabularies
// ============================================================

const RDA_VOCABULARIES_KEY = "catalog-rda-vocabularies";

/**
 * RDA-FR content, media and carrier types with their French labels.
 * Static reference data served by the backend, so it is never refetched.
 */
export function useRdaVocabularies() {
  return useQuery({
    queryKey: [RDA_VOCABULARIES_KEY],
    queryFn: () => get<RdaVocabularies>("/catalog/rda-vocabularies"),
    staleTime: Infinity,
  });
}

// ============================================================
// Import hooks
// ============================================================

const IMPORT_FORMATS_KEY = "catalog-import-formats";

/**
 * Get supported import formats.
 */
export function useImportFormats() {
  return useQuery({
    queryKey: [IMPORT_FORMATS_KEY],
    queryFn: async () => {
      const response = await get<{ formats: Record<string, ImportFormat> }>(
        "/catalog/import/formats"
      );
      return response.formats;
    },
  });
}

/**
 * Validate an import file before importing.
 */
export function useValidateImport() {
  return useMutation({
    mutationFn: async (file: File) => {
      return uploadFile<ImportValidationResult>("/catalog/import/validate", file);
    },
  });
}

/**
 * Import records from a file.
 */
export function useImportRecords() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, dryRun = false }: { file: File; dryRun?: boolean }) => {
      return uploadFile<ImportResult>("/catalog/import", file, {
        dry_run: dryRun ? "1" : "0",
      });
    },
    onSuccess: (data) => {
      // Only invalidate if not a dry run and records were imported
      if (!data.dryRun && data.report.successCount > 0) {
        void queryClient.invalidateQueries({ queryKey: [RECORDS_KEY] });
      }
    },
  });
}
